import { ArrowRight, ArrowsLeftRight, CreditCard, Globe } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { api, unwrap } from '../api.ts';
import { ErrorNotice } from '../common.tsx';

function RequestRow({ name, kind, id }: { name: string; kind: 'site' | 'billing' | 'transfer'; id: string }) {
  const label =
    kind === 'site'
      ? 'Website invitation'
      : kind === 'billing'
        ? 'Payment-method access'
        : 'Ownership transfer';
  const Icon = kind === 'site' ? Globe : kind === 'billing' ? CreditCard : ArrowsLeftRight;
  const destination =
    kind === 'transfer'
      ? { to: '/transfers/$transferId' as const, params: { transferId: id } }
      : { to: '/invitations/$kind/$invitationId' as const, params: { kind, invitationId: id } };
  return (
    <li>
      <Link
        {...destination}
        aria-label={`Review ${label.toLowerCase()}: ${name}`}
        className="group flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-kumo-tint focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-kumo-brand"
      >
        <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-kumo-tint text-kumo-subtle group-hover:text-kumo-brand">
          <Icon size={17} aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-medium text-kumo-default">{name}</span>
          <span className="block text-[11px] leading-4 text-kumo-subtle">{label}</span>
        </span>
        <span className="flex shrink-0 items-center gap-1.5 text-[12px] font-medium text-kumo-subtle group-hover:text-kumo-brand">
          Review <ArrowRight size={14} aria-hidden="true" />
        </span>
      </Link>
    </li>
  );
}

export function PendingRequests({ className = '' }: { className?: string }) {
  const invitations = useQuery({
    queryKey: ['invitations'],
    queryFn: async () => {
      const [site, billing] = await Promise.all([
        api.GET('/api/site-invitations'),
        api.GET('/api/billing-invitations'),
      ]);
      return [...unwrap(site), ...unwrap(billing)].sort((a, b) => a.expiresAt.localeCompare(b.expiresAt));
    },
  });
  const transfers = useQuery({
    queryKey: ['incoming-transfers'],
    queryFn: async () => unwrap(await api.GET('/api/transfers')),
  });
  const incoming =
    transfers.data?.filter((transfer) =>
      ['Pending', 'Preparing', 'AwaitingPaymentMethod', 'AwaitingPayment', 'Committing'].includes(
        transfer.status,
      ),
    ) ?? [];
  const hasRequests = Boolean(invitations.data?.length || incoming.length);
  return (
    <>
      <ErrorNotice error={invitations.error} />
      <ErrorNotice error={transfers.error} />
      {hasRequests && (
        <section
          aria-label="Invitations"
          className={`overflow-hidden rounded-xl border border-kumo-line bg-kumo-base ${className}`}
        >
          <div className="border-b border-kumo-line px-4 py-2.5">
            <h2 className="m-0 text-[12px] font-medium text-kumo-subtle">Invitations</h2>
          </div>
          <ul className="m-0 list-none divide-y divide-kumo-line p-0">
            {invitations.data?.map((invitation) => (
              <RequestRow
                key={`${invitation.kind}:${invitation.id}`}
                id={invitation.id}
                kind={invitation.kind === 'Site' ? 'site' : 'billing'}
                name={
                  invitation.kind === 'Site'
                    ? invitation.siteName || 'Untitled site'
                    : invitation.ownerName.trim() || invitation.ownerEmail
                }
              />
            ))}
            {incoming.map((transfer) => (
              <RequestRow key={transfer.id} id={transfer.id} kind="transfer" name={transfer.siteName} />
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
